/**
 * V2 source path: customer/midas/components/v2/midasMessageFeedbackV2Slice.ts
 * V1 source: scratch (Phase 1 local-only feedback state — Phase 5 will replace
 *   this slice with thunks that POST to bot endpoints once contracts land)
 * Strategy: scratch-with-reused-handlers
 * Verified no-MUI: yes
 * Phase: 1
 *
 * Per `MIDAS_REDESIGN_ROLLOUT_PLAN.md` Phase 1, Like / Dislike / Retry are
 * stored in local Redux state keyed by message id. Like and Dislike are
 * mutually exclusive — toggling one clears the other. Retry sets a transient
 * flag the chat area can read to show feedback toast or replay the message
 * (Phase 1 keeps it visually-active no-op; Phase 5 wires the bot endpoint).
 *
 * Survives navigation within the session but NOT page reload — intentional.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface MessageFeedbackEntry {
  liked: boolean;
  disliked: boolean;
  retryRequested: boolean;
}

interface MidasMessageFeedbackV2State {
  byMessageId: Record<string, MessageFeedbackEntry>;
}

const initialState: MidasMessageFeedbackV2State = {
  byMessageId: {},
};

const midasMessageFeedbackV2Slice = createSlice({
  name: "midasMessageFeedbackV2",
  initialState,
  reducers: {
    toggleLike: (state, action: PayloadAction<{ messageId: string }>) => {
      const id = action.payload.messageId;
      const prev = state.byMessageId[id] || {
        liked: false,
        disliked: false,
        retryRequested: false,
      };
      state.byMessageId[id] = {
        ...prev,
        liked: !prev.liked,
        // Mutually exclusive — clear dislike on like
        disliked: prev.liked ? prev.disliked : false,
      };
    },
    toggleDislike: (state, action: PayloadAction<{ messageId: string }>) => {
      const id = action.payload.messageId;
      const prev = state.byMessageId[id] || {
        liked: false,
        disliked: false,
        retryRequested: false,
      };
      state.byMessageId[id] = {
        ...prev,
        disliked: !prev.disliked,
        liked: prev.disliked ? prev.liked : false,
      };
    },
    requestRetry: (state, action: PayloadAction<{ messageId: string }>) => {
      const id = action.payload.messageId;
      const prev = state.byMessageId[id] || {
        liked: false,
        disliked: false,
        retryRequested: false,
      };
      state.byMessageId[id] = {
        ...prev,
        retryRequested: true,
      };
    },
    clearRetry: (state, action: PayloadAction<{ messageId: string }>) => {
      const id = action.payload.messageId;
      const prev = state.byMessageId[id];
      if (prev) {
        state.byMessageId[id] = { ...prev, retryRequested: false };
      }
    },
  },
});

export const { toggleLike, toggleDislike, requestRetry, clearRetry } =
  midasMessageFeedbackV2Slice.actions;

export default midasMessageFeedbackV2Slice.reducer;
